import {observable, action, computed, runInAction} from 'mobx';
import {getRootStore} from '../../../app.bootstrap';
import {ITranslate} from '../../../services/LocalizationService';
import {ScanStatus} from '../../../services/AngularServices/WifiAnalyzerService';
import {ReportType} from '../../../services/AngularServices/WifiAnalyzerService';

export interface IPollingBirthCertificateReportController {
    readonly translate: ITranslate;
    readonly scanId: string | null;
    readonly reportType: ReportType | null;
    readonly isError: boolean;
    readonly isSpinner: boolean;
    readonly reportUrl: string | null;

    setReport(scanId: string, reportType: ReportType): void;
    showSpinner(): void;
    hideSpinner(): void;
    setIsError(value: boolean): void;
    setReportUrl(url: string | null): void;
    generateReport(): Promise<{success: boolean; url: string | null}>;
}

export class PollingBirthCertificateReportController implements IPollingBirthCertificateReportController {
    private readonly _translate: ITranslate;

    @observable private _scanId: string | null = null;
    @observable private _isCancelled: boolean = false;
    @observable private _isError: boolean = false;
    @observable private _reportType: ReportType | null = null;
    @observable private _isSpinnerVisible: boolean = false;
    @observable private _reportUrl: string | null = null;

    constructor(translate: ITranslate) {
        this._translate = translate;
        this.setReport = this.setReport.bind(this);
        this.setIsError = this.setIsError.bind(this);
        this.generateReport = this.generateReport.bind(this);
        this.showSpinner = this.showSpinner.bind(this);
        this.hideSpinner = this.hideSpinner.bind(this);
        this.setReportUrl = this.setReportUrl.bind(this);
    }

    get translate(): ITranslate {
        return this._translate;
    }

    @action
    setReport(scanId: string, reportType: ReportType): void {
        if (!scanId) {
            return;
        }

        this._scanId = scanId;
        this._reportType = reportType;
        this._isCancelled = false;
        this._isError = false;
    }

    @action
    showSpinner(): void {
        this._isSpinnerVisible = true;
    }

    @action
    hideSpinner(): void {
        this._isSpinnerVisible = false;
    }

    @action
    setIsError(value: boolean): void {
        this._isError = value;
    }

    @action
    setReportUrl(url: string | null): void {
        this._reportUrl = url;
    }

    @computed
    get scanId(): string | null {
        return this._scanId;
    }

    @computed
    get isError(): boolean {
        return this._isError;
    }

    @computed
    get reportType(): ReportType | null {
        return this._reportType;
    }

    @computed
    get isSpinner(): boolean {
        return this._isSpinnerVisible;
    }

    @computed
    get reportUrl(): string | null {
        return this._reportUrl;
    }

    async generateReport(): Promise<{success: boolean; url: string | null}> {
        if (!this._scanId || !this._reportType) {
            return Promise.reject(new Error('Missing scanId or reportType'));
        }

        try {
            const result = await getRootStore().wifiAnalyzerService.triggerReportGeneration(
                this._scanId,
                this._reportType
            );

            if (this._isCancelled) {
                return Promise.resolve({success: false, url: null});
            }

            if (result.status === ScanStatus.READY && result.url) {
                this.setReportUrl(result.url);
                return Promise.resolve({success: true, url: result.url});
            }

            const url = await getRootStore().wifiAnalyzerService.pollUntilReady(
                this._scanId,
                this._reportType,
                () => this._isCancelled
            );

            if (!this._isCancelled) {
                this.setReportUrl(url);
                return Promise.resolve({success: true, url});
            }

            return Promise.resolve({success: false, url: null});
        } catch (err) {
            if (!this._isCancelled) {
                runInAction(() => {
                    this._isError = true;
                });
            }
            return Promise.reject(err);
        }
    }
}
